/**
 * PaymentMethodPill - Draggable list item for payment method ordering
 *
 * Composed component: No internal dependencies.
 *
 * Features:
 * - Hamburger drag handle on left
 * - Drag only via handle (not whole item)
 * - Visual feedback during drag
 */

import { useState } from 'react';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PaymentMethodPillProps {
  /** Unique identifier */
  id: string;
  /** Payment method name */
  name: string;
  /** Current position index (0 = disabled/inactive) */
  index: number;
  /** Whether this method is active (inactive methods are not draggable, position 0) */
  isActive?: boolean;
  /** Drag start handler */
  onDragStart?: (id: string, index: number) => void;
  /** Drag over handler */
  onDragOver?: (id: string, index: number) => void;
  /** Drag end handler */
  onDragEnd?: () => void;
  /** Whether this item is being dragged */
  isDragging?: boolean;
  /** Whether another item is being dragged over this one */
  isDragOver?: boolean;
  /** Disabled state (entire component disabled) */
  disabled?: boolean;
}

export function PaymentMethodPill({
  id,
  name,
  index,
  isActive = true,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragging = false,
  isDragOver = false,
  disabled = false,
}: PaymentMethodPillProps) {
  const [isHandlePressed, setIsHandlePressed] = useState(false);

  // Inactive methods cannot be dragged
  const canDrag = isActive && !disabled;

  const handleDragStart = (e: React.DragEvent) => {
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    onDragStart?.(id, index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isActive) return; // Don't allow dropping on inactive items
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver?.(id, index);
  };

  const handleDragEnd = () => {
    setIsHandlePressed(false);
    onDragEnd?.();
  };

  return (
    <div
      draggable={isHandlePressed && canDrag}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDrop={(e) => e.preventDefault()}
      className={cn(
        'flex items-center gap-3 w-full',
        'px-3 py-3 rounded-full',
        'bg-white',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'border border-slate-200',
        'transition-all duration-200',
        // Dragging state
        isDragging && 'opacity-50 scale-95',
        // Drag over state (only for active items)
        isDragOver && isActive && 'border-sky-400 border-2 bg-sky-50',
        // Inactive state (position 0, not draggable)
        !isActive && 'bg-slate-50 opacity-60',
        // Disabled
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      {/* Drag Handle - hidden/disabled for inactive */}
      <button
        type="button"
        onMouseDown={() => canDrag && setIsHandlePressed(true)}
        onMouseUp={() => setIsHandlePressed(false)}
        onMouseLeave={() => setIsHandlePressed(false)}
        onTouchStart={() => canDrag && setIsHandlePressed(true)}
        onTouchEnd={() => setIsHandlePressed(false)}
        disabled={!canDrag}
        className={cn(
          'p-1 rounded-md',
          'transition-colors',
          canDrag
            ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-grab'
            : 'text-slate-300 cursor-not-allowed',
          isHandlePressed && canDrag && 'cursor-grabbing text-slate-600 bg-slate-100',
        )}
        aria-label={canDrag ? 'Drag to reorder' : 'Inactive - cannot reorder'}
      >
        <GripVertical className="w-5 h-5" />
      </button>

      {/* Method Name */}
      <span className={cn(
        'flex-1 text-sm font-medium truncate',
        isActive ? 'text-slate-700' : 'text-slate-400',
      )}>
        {name}
      </span>

      {/* Position indicator - shows "-" for inactive (position 0) */}
      <span className={cn(
        'text-xs tabular-nums',
        isActive ? 'text-slate-400' : 'text-slate-300',
      )}>
        {isActive ? `#${index + 1}` : '-'}
      </span>
    </div>
  );
}

/**
 * PaymentMethodPillList - Container with drag-and-drop state management
 *
 * Business logic:
 * - Active items (isActive=true) have positions 1, 2, 3... and are draggable
 * - Inactive items (isActive=false) have position 0, shown at bottom, NOT draggable
 * - When toggling active, item gets next position (becomes last active)
 */
export interface PaymentMethodItem {
  id: string;
  name: string;
  isActive: boolean;
}

export interface PaymentMethodPillListProps {
  /** List of payment methods */
  items: PaymentMethodItem[];
  /** Callback when order changes (only called for active items reorder) */
  onReorder: (items: PaymentMethodItem[]) => void;
  /** Disabled state (entire list disabled) */
  disabled?: boolean;
  /** Optional className */
  className?: string;
}

export function PaymentMethodPillList({
  items,
  onReorder,
  disabled = false,
  className,
}: PaymentMethodPillListProps) {
  // Split and sort: active items first (by their order), inactive at bottom
  const activeItems = items.filter((item) => item.isActive);
  const inactiveItems = items.filter((item) => !item.isActive);

  const [dragState, setDragState] = useState<{
    draggingId: string | null;
    draggingIndex: number;
    overIndex: number | null;
  }>({
    draggingId: null,
    draggingIndex: -1,
    overIndex: null,
  });

  const handleDragStart = (id: string, index: number) => {
    setDragState({
      draggingId: id,
      draggingIndex: index,
      overIndex: null,
    });
  };

  const handleDragOver = (_id: string, index: number) => {
    if (dragState.draggingId && index !== dragState.overIndex) {
      setDragState((prev) => ({ ...prev, overIndex: index }));
    }
  };

  const handleDragEnd = () => {
    const { draggingIndex, overIndex } = dragState;

    if (overIndex !== null && draggingIndex !== overIndex) {
      // Reorder only the active items
      const newActiveItems = [...activeItems];
      const [draggedItem] = newActiveItems.splice(draggingIndex, 1);
      if (draggedItem) {
        newActiveItems.splice(overIndex, 0, draggedItem);
      }

      // Only send active items to onReorder - inactive items have position 0
      // and don't need to be included in reorder request
      onReorder(newActiveItems);
    }

    setDragState({
      draggingId: null,
      draggingIndex: -1,
      overIndex: null,
    });
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Active items - draggable */}
      {activeItems.map((item, index) => (
        <PaymentMethodPill
          key={item.id}
          id={item.id}
          name={item.name}
          index={index}
          isActive={true}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          isDragging={dragState.draggingId === item.id}
          isDragOver={dragState.overIndex === index && dragState.draggingId !== item.id}
          disabled={disabled}
        />
      ))}

      {/* Separator if there are both active and inactive */}
      {activeItems.length > 0 && inactiveItems.length > 0 && (
        <div className="flex items-center gap-2 py-1">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">Inactive</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
      )}

      {/* Inactive items - NOT draggable, position 0 */}
      {inactiveItems.map((item) => (
        <PaymentMethodPill
          key={item.id}
          id={item.id}
          name={item.name}
          index={0}
          isActive={false}
          disabled={disabled}
        />
      ))}
    </div>
  );
}
